import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';
import {hasAdminPermission} from '@afriex/core/utils';

import {Button, Header, Pagination, Select, TableSkeleton} from '@/components';
import type {AdminPinResetDecisionStatus, AdminPinResetRequest} from '@/types';
import {BusinessPinResetRequestStatus} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import {PinResetDecisionModal} from './PinResetDecisionModal';
import {PinResetRequestsTable} from './PinResetRequestsTable';
import {usePinResetRequests} from './usePinResetRequests';

const statusOptions = Object.values(BusinessPinResetRequestStatus).map(
  (status) => ({key: status, value: status}),
);

/**
 * The operations side of a dual-control PIN reset: businesses ask, operations
 * reviews, and only an approval clears the PIN. A decision is finance-only, so
 * every other admin role sees the queue read-only.
 */
const PinResetRequestsScreen = () => {
  const hook = usePinResetRequests();
  const [decision, setDecision] = useState<{
    request: AdminPinResetRequest;
    status: AdminPinResetDecisionStatus;
  } | null>(null);

  // Reading the queue is open to every staff role; clearing an account's PIN
  // is not, so the decision carries its own permission.
  const canDecide = hasAdminPermission(
    localStorage.getItem('admin-role') ?? undefined,
    AfriexAdminPermission.BusinessPinResetRequestReview,
  );

  const handleConfirm = async (note?: string) => {
    if (!decision?.request.id) {
      return;
    }
    try {
      await hook.decide(decision.request.id, {
        status: decision.status,
        ...(note ? {note} : {}),
      });
      showSuccessMessage(
        decision.status === BusinessPinResetRequestStatus.Approved
          ? 'PIN reset approved'
          : 'PIN reset request declined',
      );
      setDecision(null);
    } catch (error) {
      showErrorMessage(
        getErrorMessage(error, 'Unable to record the PIN reset decision'),
      );
    }
  };

  const renderQueue = () => {
    if (hook.isLoading) {
      return <TableSkeleton />;
    }
    if (hook.loadFailed) {
      return (
        <div className="py-8 text-center">
          <p
            data-cy="pin-reset-load-error"
            className="mb-3 text-sm text-red-600 dark:text-red-400"
          >
            Couldn&apos;t load PIN reset requests. Please try again.
          </p>
          <Button
            variant="outline"
            colorScheme="cyan"
            dataCy="pin-reset-load-retry"
            onClick={() => hook.fetchList({page: hook.displayPage - 1})}
          >
            Try again
          </Button>
        </div>
      );
    }
    if (!hook.list.length) {
      return (
        <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
          No PIN reset requests found.
        </p>
      );
    }
    return (
      <PinResetRequestsTable
        items={hook.list}
        canDecide={canDecide}
        isDeciding={hook.isDeciding}
        onDecide={(request, status) => setDecision({request, status})}
      />
    );
  };

  return (
    <main className="my-5 mx-4 md:mx-10">
      <div className="flex items-center justify-between">
        <Header title="PIN Reset Requests" />
        <div className="w-56">
          <Select
            name="status"
            value={hook.status}
            placeholder="Status"
            options={statusOptions}
            dataCy="pin-reset-status-filter"
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
              hook.selectStatus(
                event.target.value as BusinessPinResetRequestStatus,
              )
            }
          />
        </div>
      </div>

      <div className="mt-4">{renderQueue()}</div>

      {hook.list.length > 0 && (
        <Pagination
          onPageChange={hook.fetchList}
          total={hook.total}
          isPageReset={hook.isPageReset}
          currentPageOverride={hook.displayPage}
        />
      )}

      <PinResetDecisionModal
        // Remounted per request so a note typed for one never prefills the next.
        key={decision?.request.id ?? 'none'}
        isOpen={Boolean(decision)}
        request={decision?.request ?? null}
        status={decision?.status ?? null}
        isSubmitting={hook.isDeciding}
        onClose={() => setDecision(null)}
        onConfirm={handleConfirm}
      />
    </main>
  );
};

export default PinResetRequestsScreen;
